import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Input, Row, Select, Table, Tag, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useLazyQuery } from '@apollo/client';
import TablePaginationBar from 'components/TablePaginationBar';
import { HasResource } from 'components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';
import {
    DISPATCH_EVENT_TASK_ORDERS,
    DispatchEventTaskOrderFilter,
    DispatchEventTaskOrderItem,
    DispatchEventTaskOrdersResponse,
} from '../graphql/queries/dispatchToolQueries';
import OrderFieldUpdateModal, { OrderFieldUpdateTarget } from './OrderFieldUpdateModal';
import {
    COMPLETED_OPTIONS,
    DEFAULT_PAGINATION,
    MAX_RETRY_COUNT,
    PAGE_SIZE_OPTIONS,
    RETRY_COUNT_OPTIONS,
    parseIdInput,
    renderDateTime,
    renderEventStatus,
    renderFlag,
} from './dispatchToolShared';


type FilterValues = {
    dispatchEventTaskId?: string;
    orderId?: string;
    bagId?: string;
    isCompleted?: number;
    isSentMq?: number;
    retryCount?: number;
};

type Props = {
    /** Tab1「查看订单明细」联动传入的调度事件任务ID */
    linkedTaskId?: number;
    /** 联动触发序号（同一 ID 连续点击也要重新触发查询） */
    linkSeq?: number;
};

const DispatchEventTaskOrderTab: React.FC<Props> = ({ linkedTaskId, linkSeq }) => {
    const [form] = Form.useForm<FilterValues>();
    // 首挂载若由联动进入，初始条件即联动任务ID，避免先按默认条件多查一次
    const [activeFilter, setActiveFilter] = useState<DispatchEventTaskOrderFilter>(() =>
        linkedTaskId !== undefined ? { dispatchEventTaskId: linkedTaskId } : {},
    );
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    // 「更新字段」弹窗：操作列单条更新 / 勾选后批量更新
    const [updateTarget, setUpdateTarget] = useState<OrderFieldUpdateTarget | null>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const handledSeq = useRef(linkSeq);
    const [runQuery, { data, loading }] = useLazyQuery<DispatchEventTaskOrdersResponse>(DISPATCH_EVENT_TASK_ORDERS, {
        fetchPolicy: 'network-only',
        onError: (e) => message.error(e.message),
    });

    useEffect(() => {
        setSelectedRowKeys([]);
        runQuery({
            variables: {
                filter: activeFilter,
                pageRequest: { page: pagination.current, size: pagination.pageSize },
            },
        });
    }, [activeFilter, pagination, runQuery]);

    useEffect(() => {
        if (linkedTaskId === undefined || linkSeq === undefined) return;
        if (handledSeq.current === linkSeq) return;
        handledSeq.current = linkSeq;
        form.setFieldsValue({
            dispatchEventTaskId: String(linkedTaskId),
            orderId: undefined,
            bagId: undefined,
            isCompleted: undefined,
            isSentMq: undefined,
        });
        setPagination((prev) => ({ ...prev, current: 1 }));
        setActiveFilter({ dispatchEventTaskId: linkedTaskId });
    }, [linkedTaskId, linkSeq, form]);

    const handleSearch = () => {
        const values = form.getFieldsValue();
        const dispatchEventTaskId = parseIdInput(values.dispatchEventTaskId);
        const orderId = parseIdInput(values.orderId);
        const bagId = parseIdInput(values.bagId);
        if (dispatchEventTaskId === null || orderId === null || bagId === null) {
            message.warning('ID 必须是纯数字');
            return;
        }
        setPagination((prev) => ({ ...prev, current: 1 }));
        setActiveFilter({
            dispatchEventTaskId,
            orderId,
            bagId,
            isCompleted: values.isCompleted,
            isSentMq: values.isSentMq,
            retryCount: values.retryCount,
        });
    };

    const columns: ColumnsType<DispatchEventTaskOrderItem> = [
        { title: 'ID', dataIndex: 'id', width: 90 },
        { title: '调度任务ID', dataIndex: 'dispatchEventTaskId', width: 100 },
        { title: '任务ID', dataIndex: 'taskId', width: 90 },
        { title: '途经点ID', dataIndex: 'taskWaypointId', width: 100 },
        { title: '订单ID', dataIndex: 'orderId', width: 100 },
        { title: '包牌ID', dataIndex: 'bagId', width: 90, render: (v) => v ?? '-' },
        { title: '发车', dataIndex: 'departureEventStatus', width: 90, render: renderEventStatus },
        { title: '到达', dataIndex: 'arrivedEventStatus', width: 90, render: renderEventStatus },
        { title: 'Handover', dataIndex: 'handoverEventStatus', width: 90, render: renderEventStatus },
        { title: 'Tender', dataIndex: 'tenderEventStatus', width: 90, render: renderEventStatus },
        { title: '卸车', dataIndex: 'unloadingEventStatus', width: 90, render: renderEventStatus },
        { title: 'LogScan', dataIndex: 'logscanEventStatus', width: 90, render: renderEventStatus },
        { title: '装车移出', dataIndex: 'removalEventStatus', width: 90, render: renderEventStatus },
        { title: '已完成', dataIndex: 'isCompleted', width: 80, render: (v: number) => renderFlag(v) },
        { title: '已发MQ', dataIndex: 'isSentMq', width: 80, render: (v: number) => renderFlag(v) },
        {
            title: '重试次数',
            dataIndex: 'retryCount',
            width: 90,
            render: (v: number) =>
                v >= MAX_RETRY_COUNT ? <span style={{ color: '#ff4d4f' }}>{v}（已达上限）</span> : v,
        },
        {
            title: '异常信息',
            dataIndex: 'exceptionMsg',
            width: 200,
            ellipsis: { showTitle: false },
            render: (v: string | null) =>
                v ? (
                    <Tooltip title={v} placement="topLeft">
                        <span>{v}</span>
                    </Tooltip>
                ) : (
                    '-'
                ),
        },
        { title: '创建时间', dataIndex: 'createdAt', width: 160, render: renderDateTime },
        { title: '更新时间', dataIndex: 'updatedAt', width: 160, render: renderDateTime },
        {
            title: '操作',
            key: 'action',
            width: 100,
            fixed: 'right',
            render: (_, record) => (
                <HasResource resourceCodes={[ResourceCode.menu.OPS_TOOL_TMS]}>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => setUpdateTarget({ kind: 'order', orderRowId: record.id })}
                    >
                        更新字段
                    </Button>
                </HasResource>
            ),
        },
    ];

    const rows = data?.dispatchEventTaskOrders.content ?? [];
    const totalElements = data?.dispatchEventTaskOrders.totalElements ?? 0;

    return (
        <div>
            <Form
                form={form}
                initialValues={{
                    dispatchEventTaskId: linkedTaskId !== undefined ? String(linkedTaskId) : undefined,
                }}
            >
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item name="dispatchEventTaskId" label="调度任务ID">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="orderId" label="订单ID">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="bagId" label="包牌ID">
                            <Input allowClear placeholder="精确匹配（无索引，建议配合ID条件查询）" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="isCompleted" label="完成状态">
                            <Select allowClear placeholder="全部" options={COMPLETED_OPTIONS} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="isSentMq" label="是否发MQ">
                            <Select
                                allowClear
                                placeholder="全部（单独无索引，建议配合完成状态或ID条件）"
                                options={[
                                    { value: 1, label: '是' },
                                    { value: 0, label: '否' },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="retryCount" label="重试次数">
                            <Select allowClear placeholder="全部（无索引，建议配合ID条件查询）" options={RETRY_COUNT_OPTIONS} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item>
                            <HasResource resourceCodes={[ResourceCode.menu.OPS_TOOL_TMS]}>
                                <Button
                                    disabled={selectedRowKeys.length === 0}
                                    onClick={() => {
                                        if (selectedRowKeys.length > 500) {
                                            message.warning('批量更新单次最多 500 条，请减少勾选');
                                            return;
                                        }
                                        setUpdateTarget({ kind: 'orders', orderRowIds: selectedRowKeys.map(Number) });
                                    }}
                                >
                                    批量更新字段
                                </Button>
                                {selectedRowKeys.length > 0 && (
                                    <span className="text-gray-500 ml-2">已选 {selectedRowKeys.length} 条</span>
                                )}
                            </HasResource>
                        </Form.Item>
                    </Col>
                    <Col span={8} className="ml-auto text-right">
                        <Form.Item>
                            <Button className="mr-2" onClick={() => form.resetFields()}>
                                重置
                            </Button>
                            <Button type="primary" loading={loading} onClick={handleSearch}>
                                查询
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <Table<DispatchEventTaskOrderItem>
                rowKey="id"
                size="small"
                loading={loading}
                columns={columns}
                dataSource={rows}
                pagination={false}
                scroll={{ x: 1790 }}
                className="mb-4"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys,
                }}
            />
            <TablePaginationBar
                total={totalElements}
                current={pagination.current}
                pageSize={pagination.pageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onChange={(page, size) => setPagination({ current: page, pageSize: size })}
            />
            <OrderFieldUpdateModal
                target={updateTarget}
                onClose={() => setUpdateTarget(null)}
                onSuccess={() => setPagination((prev) => ({ ...prev }))}
            />
        </div>
    );
};

export default DispatchEventTaskOrderTab;
